import type { SearchLocation } from '@gumtree/shared/src/model/search-location';
import {
    cleanLocationCity,
    getLocationCity,
    isSameLocationIdentity,
    mergeLocationCity,
} from '@gumtree/shared/src/util/location-metadata';
import { setValue } from '@gumtree/ui-library/src/utils/local-storage-service';
import { baseFetch } from '@gumtree/shared/src/util/base-fetch';
import { getGA4ClientId } from '@gumtree/shared/src/util/ga4-shared';
import { getSharedCookieDomain } from '@gumtree/shared/src/util/ga4-postponer';
import {
    encodeRecentLocPref,
    RECENT_LOC_PREF_COOKIE,
} from '@gumtree/shared/src/util/recent-location-pref-cookie';
import { clearAllByKey, loadByKey, removeSingleByKey, saveByKey } from './recent-service';

export const RECENT_LOCATIONS_KEY = 'recentLocations';
const SESSION_STORAGE_KEY = 'recentLocations_apiFetched';
const SESSION_STARTED_KEY = 'recentLocations_sessionStarted';
const SESSION_CACHE_KEY = 'recentLocations_cache';
const STORAGE_MAX = 10;

let apiFetchInFlight: Promise<RecentLocationsItem[] | null> | null = null;

export type RecentLocationsItem = SearchLocation;

export interface RecentLocationRecord {
    location_id: string;
    location_name: string;
    location_type: string;
    create_ts: number;
    latitude?: number;
    longitude?: number;
    city?: string;
}

export interface RecentLocationsApiResponse {
    code: number;
    message: string;
    data: {
        locations: RecentLocationRecord[];
    } | null;
}

/**
 * Get cache from sessionStorage (persists across page loads)
 */
const getSessionCache = (): RecentLocationsItem[] | null => {
    try {
        const cached = sessionStorage.getItem(SESSION_CACHE_KEY);
        const parsed = cached ? JSON.parse(cached) : null;
        return Array.isArray(parsed)
            ? parsed.filter((item) => item && typeof item.name === 'string').map(cleanLocationCity)
            : null;
    } catch {
        return null;
    }
};

/**
 * Save cache to sessionStorage (persists across page loads)
 */
const setSessionCache = (data: RecentLocationsItem[]) => {
    try {
        sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(data));
    } catch {
        // Ignore
    }
};

/**
 * Check if we already fetched API this browser session
 */
const hasApiFetchedThisSession = (): boolean => {
    try {
        return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

/**
 * Whether this tab session has already established location context (URL location page
 * visited or recent-locations API initiated). Set before API completes to avoid races
 * when navigating quickly between pages.
 */
export const hasRecentLocationsSessionStarted = (): boolean => {
    try {
        return sessionStorage.getItem(SESSION_STARTED_KEY) === 'true';
    } catch {
        return false;
    }
};

export const markRecentLocationsSessionStarted = () => {
    try {
        sessionStorage.setItem(SESSION_STARTED_KEY, 'true');
    } catch {
        // Ignore
    }
};

/**
 * Mark that we fetched API this session
 */
const markApiFetched = () => {
    try {
        sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    } catch {
        // Ignore
    }
};

/**
 * Clear API fetched flag (for login events)
 */
const clearApiFetchedFlag = () => {
    try {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        sessionStorage.removeItem(SESSION_STARTED_KEY);
        sessionStorage.removeItem(SESSION_CACHE_KEY);
    } catch {
        // Ignore
    }
};

/**
 * Save a new location - optimistically updates cache
 */
export const save = (locations: RecentLocationsItem) => {
    const cachedData = getSessionCache();
    const saved = mergeLocationCity(locations, [...(cachedData ?? []), ...load(STORAGE_MAX)]);

    if (hasApiFetchedThisSession() && cachedData) {
        // Optimistic update: add to session cache
        const updated = [saved, ...cachedData.filter((item) => item.name !== locations.name)].slice(
            0,
            STORAGE_MAX
        );
        setSessionCache(updated);
    } else {
        // Remove any existing entry with the same name first (saveByKey uses deep equality,
        // which would treat { name: "London", type: "Area" } and { name: "London" } as different)
        try {
            removeSingleByKey(RECENT_LOCATIONS_KEY, locations.name);
            // Not using API data, save to localStorage.
            saveByKey(RECENT_LOCATIONS_KEY, saved, STORAGE_MAX);
        } catch {
            // Storage is optional; do not block search navigation.
        }
    }

    syncRecentLocPrefCookie();
};

export const load = (limit = 5): RecentLocationsItem[] => {
    try {
        return (loadByKey(RECENT_LOCATIONS_KEY, limit) ?? [])
            .filter((item) => item && typeof item.name === 'string')
            .map(cleanLocationCity);
    } catch {
        return [];
    }
};

// Metadata-only updates retain membership, ordering and all non-city fields.
export const updateCity = (location: RecentLocationsItem) => {
    const city = getLocationCity(location.city);
    if (city === undefined) return;
    const update = (items: RecentLocationsItem[]) =>
        items.map((item) => (isSameLocationIdentity(item, location) ? { ...item, city } : item));
    const cached = getSessionCache();
    if (cached) setSessionCache(update(cached));
    try {
        setValue(RECENT_LOCATIONS_KEY, update(load(STORAGE_MAX)));
    } catch {
        // Optional metadata persistence must not block the current search.
    }
};

const fetchRecentLocationsFromApi = async (
    userId: string
): Promise<RecentLocationsItem[] | null> => {
    const response = (await baseFetch(
        `/bff-api/users/get_recent_records?user_id=${userId}&action_type=location`,
        {
            method: 'GET',
            headers: {
                Accept: 'application/json',
            },
        }
    )) as RecentLocationsApiResponse;

    // code 0 = success with data, code 20401 = no records found (treat as empty, not an error)
    const isSuccessOrEmpty = response.code === 0 || response.code === 20401;

    if (!isSuccessOrEmpty) {
        console.warn('[Recent Locations] ⚠️ API returned unexpected error code:', response.code);
        return null;
    }

    markApiFetched();

    const previous = [...(getSessionCache() ?? []), ...load(STORAGE_MAX)];
    const locations = (response.data?.locations ?? [])
        .filter((location) => !!location.location_name)
        .map((location) =>
            mergeLocationCity(
                {
                    name: location.location_name,
                    type: location.location_type,
                    id: location.location_id,
                    city: location.city,
                },
                previous
            )
        );

    setSessionCache(locations);
    try {
        clearAllByKey(RECENT_LOCATIONS_KEY);
        locations.forEach((location, index) => {
            if (index < STORAGE_MAX) {
                saveByKey(RECENT_LOCATIONS_KEY, location, STORAGE_MAX);
            }
        });
    } catch {
        // The response and session cache remain usable if localStorage fails.
    }

    if (locations.length > 0) {
        syncRecentLocPrefCookie(undefined, locations[0].name);
    } else {
        syncRecentLocPrefCookie();
    }

    return locations;
};

/**
 * Load recent locations - API first for logged-in users (once per session), then cache
 */
export const loadWithApiSupport = async (
    userData: { userLoggedIn: boolean; id: number } | null,
    limit = 5
): Promise<RecentLocationsItem[]> => {
    const cachedData = getSessionCache();

    // FIRST: If we have session cache (from API), return it
    if (cachedData && hasApiFetchedThisSession()) {
        return cachedData.filter((item) => !!item.name).slice(0, limit);
    }

    if (hasApiFetchedThisSession()) {
        return load(limit);
    }

    // SECOND: logged-in users share one in-flight request per session
    const userId = userData?.id?.toString() ?? getGA4ClientId();

    if (userData?.userLoggedIn && userId) {
        if (!apiFetchInFlight) {
            markRecentLocationsSessionStarted();
            apiFetchInFlight = fetchRecentLocationsFromApi(userId)
                .catch((error) => {
                    console.error('[Recent Locations] ❌ API failed:', error);
                    return null;
                })
                .finally(() => {
                    apiFetchInFlight = null;
                });
        }

        const apiLocations = await apiFetchInFlight;
        if (apiLocations) {
            return apiLocations.filter((item) => !!item.name).slice(0, limit);
        }
    }

    // THIRD: Fallback to localStorage
    return load(limit);
};

const broadcastChange = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('recentLocationsChanged'));
    }
};

export const removeSingleFromHistory = (text: string) => {
    const cachedData = getSessionCache();

    if (cachedData && hasApiFetchedThisSession()) {
        // Update session cache
        const updated = cachedData.filter((item) => item.name !== text);
        setSessionCache(updated);
    }
    // Always remove from localStorage as well to keep both stores in sync
    removeSingleByKey(RECENT_LOCATIONS_KEY, text);
    syncRecentLocPrefCookie();
    broadcastChange();
};

export const clearAllFromHistory = () => {
    if (hasApiFetchedThisSession()) {
        // Clear session cache
        setSessionCache([]);
    }
    // Always clear localStorage as well to keep both stores in sync
    clearAllByKey(RECENT_LOCATIONS_KEY);
    syncRecentLocPrefCookie();
    broadcastChange();
};

/**
 * Force refresh on login
 */
export const forceApiRefresh = () => {
    // Drop any pending request so the next load starts fresh (e.g. after login or BRP prepopulation)
    apiFetchInFlight = null;
    clearApiFetchedFlag();
};

const PREFERRED_LOCATION_CACHE_KEY = 'gt_srp4_preferredLocation';
const LEGACY_SHARED_COOKIE_DOMAIN = '.gumtree.io';

const resolveUserPrefCookieDomain = (cookieDomain?: string): string =>
    cookieDomain || getSharedCookieDomain();

const getCookieDomainFromClientData = (): string | undefined =>
    typeof window !== 'undefined'
        ? (window as { clientData?: { baseConfig?: { domain?: string } } }).clientData?.baseConfig
              ?.domain
        : undefined;

const getTopRecentLocation = (): RecentLocationsItem | undefined => {
    const cachedData = getSessionCache();
    if (cachedData?.length) {
        return cachedData[0];
    }

    const fromStorage = load(1);
    return fromStorage[0];
};

const normalizeCookieDomain = (domain: string) => domain.replace(/^\./, '').toLowerCase();

/**
 * Migration cleanup:
 * clear historical `gt_userPref` scoped to `.gumtree.io` only when the target
 * write domain is different from the legacy domain.
 */
export const clearLegacyUserPrefCookieDomain = (targetDomain: string) => {
    if (typeof document === 'undefined') return;

    if (
        normalizeCookieDomain(targetDomain) === normalizeCookieDomain(LEGACY_SHARED_COOKIE_DOMAIN)
    ) {
        return;
    }

    document.cookie = `gt_userPref=; path=/; domain=${LEGACY_SHARED_COOKIE_DOMAIN}; max-age=0; SameSite=Lax`;
};

/**
 * Keep gt_recentLocPref in sync with Recent Locations[0] so BFF can read it on SSR.
 */
export const syncRecentLocPrefCookie = (cookieDomain?: string, locationName?: string) => {
    if (typeof document === 'undefined') {
        return;
    }

    const domain = resolveUserPrefCookieDomain(cookieDomain ?? getCookieDomainFromClientData());
    const topLocation = locationName ?? getTopRecentLocation()?.name;

    if (!topLocation) {
        document.cookie = `${RECENT_LOC_PREF_COOKIE}=; path=/; domain=${domain}; max-age=0; SameSite=Lax`;
        return;
    }

    const encoded = encodeRecentLocPref(topLocation);
    if (!encoded) {
        return;
    }

    document.cookie = `${RECENT_LOC_PREF_COOKIE}=${encoded}; path=/; domain=${domain}; max-age=157680000; SameSite=Lax`;
};

/**
 * Remove the `location:` segment from the `gt_userPref` cookie.
 * Called immediately to prevent stale SEO-derived locations from persisting.
 */
export const clearLocationFromUserPrefCookie = (cookieDomain?: string) => {
    if (typeof document === 'undefined') return;
    const domain = resolveUserPrefCookieDomain(cookieDomain);
    clearLegacyUserPrefCookieDomain(domain);

    const allCookies = document.cookie.split('; ');
    const userPrefCookie = allCookies.find((c) => c.startsWith('gt_userPref='));

    if (!userPrefCookie) return;

    const value = userPrefCookie.substring('gt_userPref='.length);
    const parts = value.split('|');
    const filteredParts = parts.filter((part) => !part.startsWith('location:'));

    if (filteredParts.length === parts.length) return;

    const newValue = filteredParts.join('|');
    document.cookie = `gt_userPref=${newValue}; path=/; domain=${domain}; max-age=157680000; SameSite=Lax`;
};

/**
 * Write the user's preferred location into the `gt_userPref` cookie so the
 * Buyer backend returns it as `model.core.searchLocation` on the next page load.
 * This prevents the "United Kingdom" → actual location flash.
 *
 * Pass cookieDomain from baseConfig.domain (model.core.userPreferences.domain)
 * so the client writes to the same Domain scope as Buyer Set-Cookie.
 */
export const updateLocationInUserPrefCookie = (locationName: string, cookieDomain?: string) => {
    if (typeof document === 'undefined') return;
    const domain = resolveUserPrefCookieDomain(cookieDomain);
    clearLegacyUserPrefCookieDomain(domain);

    let encodedLocation: string;
    try {
        encodedLocation = btoa(locationName);
    } catch {
        return;
    }

    const allCookies = document.cookie.split('; ');
    const userPrefCookie = allCookies.find((c) => c.startsWith('gt_userPref='));

    if (!userPrefCookie) {
        document.cookie = `gt_userPref=location:${encodedLocation}; path=/; domain=${domain}; max-age=157680000; SameSite=Lax`;
        return;
    }

    const value = userPrefCookie.substring('gt_userPref='.length);
    const parts = value.split('|');
    const hasLocation = parts.some((part) => part.startsWith('location:'));

    const updatedParts = hasLocation
        ? parts.map((part) => (part.startsWith('location:') ? `location:${encodedLocation}` : part))
        : [...parts, `location:${encodedLocation}`];

    const newValue = updatedParts.join('|');
    document.cookie = `gt_userPref=${newValue}; path=/; domain=${domain}; max-age=157680000; SameSite=Lax`;
};

/**
 * Cache the user's preferred location in localStorage so it can be read
 * synchronously in useLayoutEffect to prevent the flash on the current page load.
 */
export const cachePreferredLocation = (name: string) => {
    try {
        localStorage.setItem(PREFERRED_LOCATION_CACHE_KEY, name);
    } catch {
        // Ignore
    }
};

export const getCachedPreferredLocation = (): string | null => {
    try {
        return localStorage.getItem(PREFERRED_LOCATION_CACHE_KEY);
    } catch {
        return null;
    }
};

// Debug helper
if (typeof window !== 'undefined') {
    (window as any).debugRecentLocations = {
        getState: () => ({
            sessionCache: getSessionCache(),
            hasApiFetchedThisSession: hasApiFetchedThisSession(),
            hasSessionStarted: hasRecentLocationsSessionStarted(),
            sessionStorageFlag: sessionStorage.getItem(SESSION_STORAGE_KEY),
        }),
        reset: () => {
            clearApiFetchedFlag();
        },
    };
}
